import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import BaseColumnForm from '../base-column-form';

import { calculateAudienceFee, hasAnyPois } from 'states/resources/ads/business-logic';
import { IncludeAndExcludeSummary } from 'widgets-v5/include-and-exclude';
import Profile from 'states/profile';
import Overlay from 'widgets-v5/overlay';
import AudienceSegmentPicker from 'containers/audience-segment-picker';

import shallowCompare from 'utils/shallow-compare';

export class AudienceForm extends React.Component {
    state = {
        isMenuOpen: false,
        audienceSelectorKey: Date.now(),
    };

    handleOpen = () => {
        this.setState({ isMenuOpen: true });
    };

    handleClose = () => {
        this.setState({ isMenuOpen: false });
    };

    handleOnCopy = () => {
        this.reloadAudienceSelector();
    };

    reloadAudienceSelector = () => {
        this.setState({ audienceSelectorKey: Date.now() });
    };

    render() {
        const { ad, ads, campaign, onSave, isCTVCampaign } = this.props;

        const shouldShowAudienceOverlay = _.some([].concat(ad, ads), ad => hasAnyPois(ad));
        // Single-ad edit can store Flex geo settings. Bulk edit starts from empty
        // defaults, so writing geo there would replace POIs on every selected ad.
        const allowGeoTargetingSave = Boolean(ad) && campaign.shouldHideFlexSegments !== true;
        const fields = allowGeoTargetingSave
            ? ['audiences', 'audience_exclude', 'audience_fee', 'geo_targeting_settings']
            : ['audiences', 'audience_exclude', 'audience_fee'];

        return (
            <BaseColumnForm
                ad={ad}
                ads={ads}
                campaign={campaign}
                headline="Audiences"
                fields={fields}
                shouldRefreshPredictionsOnSave={true}
                onSave={onSave}
                onCancel={onSave}
                onCopyAd={this.handleOnCopy}
            >
                {(updateDraft, shouldShowErrors, errors, draft) => (
                    <div
                        className={cn('media-plan-form__audience-selector', {
                            'media-plan-form__audience-selector--open': this.state.isMenuOpen,
                        })}
                    >
                        <Overlay
                            disabled
                            message="Audience Segments are not available when POI Segments are selected."
                            show={shouldShowAudienceOverlay}
                        >
                            <Profile>
                                {() => {
                                    return (
                                        <AudienceSegmentPicker
                                            onChange={({
                                                includedItems,
                                                excludedItems,
                                                geo_targeting_settings,
                                            }) => {
                                                const audienceFee = calculateAudienceFee({
                                                    includedAudiences: includedItems,
                                                    excludedAudiences: excludedItems,
                                                    audienceRates: campaign.audience_rates,
                                                });
                                                const draftUpdate = {
                                                    audiences: _.map(
                                                        includedItems,
                                                        item => item.value
                                                    ),
                                                    audience_exclude: _.map(
                                                        excludedItems,
                                                        item => item.value
                                                    ),
                                                    audience_fee: audienceFee,
                                                };

                                                if (allowGeoTargetingSave) {
                                                    draftUpdate.geo_targeting_settings = geo_targeting_settings;
                                                }

                                                updateDraft(draftUpdate);
                                            }}
                                            geo_targeting_settings={draft.geo_targeting_settings}
                                            campaignId={campaign.id}
                                            includedAudiences={_.map(draft.audiences, String)}
                                            excludedAudiences={_.map(
                                                draft.audience_exclude,
                                                String
                                            )}
                                            audienceRates={campaign.audience_rates}
                                            isCTVCampaign={isCTVCampaign}
                                        />
                                    );
                                }}
                            </Profile>
                        </Overlay>
                    </div>
                )}
            </BaseColumnForm>
        );
    }
}

/**
 * Included and excluded audience names for one ad on the setup overview.
 * Names come from audienceSegmentPicker audience segments, which that page already loads.
 * Flex selections live on geo_targeting_settings and are not printed here.
 *
 * @param {object} storeState Redux state.
 * @returns {{ audienceMapping: object }} Name lookup keyed by segment id.
 */
export const AudienceSummary = connect(storeState => {
    const audienceSegments = storeState?.audienceSegmentPicker?.audienceSegments;
    const segments = Array.isArray(audienceSegments) ? audienceSegments : [];
    const audienceMapping = {};

    for (const option of segments) {
        audienceMapping[option.value] = {
            value: option.value,
            label: option.label,
            description: option.description,
        };
    }

    return {
        audienceMapping,
    };
})(
    class extends React.Component {
        shouldComponentUpdate(nextProps, nextState) {
            const shouldUpdate =
                !shallowCompare(this.props, nextProps) || !shallowCompare(this.state, nextState);
            return shouldUpdate;
        }

        render() {
            const { ad, error, audienceMapping } = this.props;

            return (
                <div>
                    {error && <div>{error}</div>}
                    {!error && audienceMapping && (
                        <IncludeAndExcludeSummary
                            includedItems={_.map(
                                ad.audiences,
                                value => audienceMapping[value]
                            ).filter(x => x)}
                            excludedItems={_.map(
                                ad.audience_exclude,
                                value => audienceMapping[value]
                            ).filter(x => x)}
                        />
                    )}
                </div>
            );
        }
    }
);
